import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import ServicesSection from './components/ServicesSection';
import TeamSection from './components/TeamSection';
import CustomerTestimonials from './components/CustomerTestimonials';
import ManufacturerPartnerships from './components/ManufacturerPartnerships';
import RequestForm from './components/RequestForm';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';
import ImportsPage from './components/ImportsPage';
import PricingPage from './components/PricingPage';
import { scrollToSection } from './utils/scrollToSection';
import logo from './assets/images/AgriPuente Logo.png';
import './App.css';

// Get the full URL for the logo in production
const getLogoUrl = () => {
  try {
    return new URL('../src/assets/images/AgriPuente Logo.png', import.meta.url).href;
  } catch (e) {
    console.warn('Fallback to direct import for logo:', e);
    return logo;
  }
};

function App() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [requestForm, setRequestForm] = useState({
    equipmentType: '',
    otherEquipment: '',
    minBudget: '',
    maxBudget: '',
    targetDate: '',
    name: '',
    email: '',
    whatsapp: '',
    details: '',
    location: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactSuccess, setContactSuccess] = useState(false);

  useEffect(() => {
    const logoUrl = getLogoUrl();
    console.log('Setting watermark with URL:', logoUrl);
    document.documentElement.style.setProperty('--watermark-image', `url("${logoUrl}")`);
  }, []);

  // Scroll to section if ?section=... is present in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (section) {
      setTimeout(() => scrollToSection(section), 100);
    }
  }, [location]);

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch('/api/request-equipment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestForm)
      });

      const data = await response.json() as { success?: boolean; error?: string; details?: string; type?: string };
      console.log('Response data:', data);
      
      if (!response.ok) {
        throw new Error(data.details || data.error || 'Failed to submit form');
      }

      setSubmitSuccess(true);
      setSubmitError(null);
      setRequestForm({
        equipmentType: '',
        otherEquipment: '',
        minBudget: '',
        maxBudget: '',
        targetDate: '',
        name: '',
        email: '',
        whatsapp: '',
        details: '',
        location: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(
        error instanceof Error 
          ? error.message 
          : 'Failed to submit form. Please try again.'
      );
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsContactSubmitting(true);
    setContactError(null);
    setContactSuccess(false);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm)
      });

      const data = await response.json() as { success?: boolean; error?: string; details?: string };
      
      if (!response.ok) {
        throw new Error(data.details || data.error || t('contact.errorMessage'));
      }

      setContactSuccess(true);
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form submission error:', error);
      setContactError(
        error instanceof Error 
          ? error.message 
          : String(error)
      );
    } finally {
      setIsContactSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-primary-light dark:bg-primary-dark text-gray-900 dark:text-white transition-colors duration-300">
      <Navbar
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        i18n={i18n}
        scrollToSection={scrollToSection}
      />
      <Routes>
        <Route
          path="/"
          element={
            <main>
              <HeroSection />
              <ServicesSection />
              <TeamSection />
              <CustomerTestimonials />
              <ManufacturerPartnerships />
              <RequestForm
                requestForm={requestForm}
                setRequestForm={setRequestForm}
                isSubmitting={isSubmitting}
                submitError={submitError}
                submitSuccess={submitSuccess}
                handleRequestSubmit={handleRequestSubmit}
              />
              <ContactForm
                contactForm={contactForm}
                setContactForm={setContactForm}
                isContactSubmitting={isContactSubmitting}
                contactError={contactError}
                contactSuccess={contactSuccess}
                handleContactSubmit={handleContactSubmit}
              />
              <Footer />
            </main>
          }
        />
        <Route path="/imports" element={<ImportsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
      </Routes>
    </div>
  );
}

export default App;
